import React, { useState, useMemo } from "react";
import AnimationContainer from "../../../Reusable/AnimationContainer/AnimationContainer.js";
import EnterControl from "./EnterControl.js";
import fileTypeGridElements from "../../../Reusable/FilesTypeElement/FilesTypeElement.js";
import Icon from "../../../Elements/Icon/Icon.tsx";
import useMediaQueries from "../../../../hooks/useMediaQueries.js";
import FilesActionGroup from "../../../Reusable/FilesActionGroup/FilesActionGroup.js";
import IconStopPropagation from "./IconStopPropagation.js";
import Tooltip from "../../../Elements/Tooltip/Tooltip.js";
import useMultiSelectionHandler from "../helpers/useMultiSelectionHandler.ts";
import useSelfRemovalLink from "hooks/useSelfRemovalLink.tsx";
import CollectionFileAction from "components/Reusable/FilesActionGroup/CollectionFileAction.js";
import DownloadActionComponent from "components/Reusable/FilesActionGroup/DownloadAction.js";
import CustomPopup from "components/Pages/Inbox/Popups/CustomPopup.js";
import { useMailView } from "../../../../contexts/mailViewContext/mailViewContext.js";
import { useMail } from "../../../../contexts/mailContext/mailContext.js";
import { useActionList } from "hooks/useActionList.js";
/**
 * gallery item within should contain logic which one the same for all kinds of files.
 * for example onhover icons, opening functions, sharing etc...
 * as children should came all what should appears inside item-container and no depend from anything what happening in item-container
 * or which want to use some data from outside the container and container will not use it
 */

const ACTIONS_NUMBER_LIMIT = 3;

export const TranscodingElement = ({ fileData }) => {
  if (!fileData || !fileData.transcodingStatus) return null;

  return (
    <>
      {fileData.transcodingStatus === "PROGRESSING" && (
        <Tooltip
          tooltipTextKey="transcoding-icon-tooltip-text"
          start
          small
          className="transcoding-icon-tooltip"
        >
          <div className="transcoding-icon">
            <Icon name="home/arrows-rotate-light" blue />
          </div>
        </Tooltip>
      )}
      {fileData.transcodingStatus === "ERROR" && (
        <Tooltip
          tooltipTextKey="transcoding-error-icon-tooltip-text"
          start
          small
          className="transcoding-error-icon-tooltip"
        >
          <div className="transcoding-error-icon">
            <Icon name="exclamation-icon" red />
          </div>
        </Tooltip>
      )}
    </>
  );
};

const GridCardHeader = ({ itemId, hover }) => {
  const { getFilesSelection, getFileSelected } = useMailView();

  const { getFilesData } = useMail();

  const isSelfRemove = useSelfRemovalLink();

  const { mobileCondition } = useMediaQueries();

  const className =
    hover || getFileSelected(itemId) || getFilesSelection().length > 0
      ? "top-section"
      : "top-section hidden";
  const handleSelectItem = useMultiSelectionHandler();

  return (
    <div className={className}>
      {hover && !mobileCondition && <CollectionFileAction itemId={itemId} />}
      {isSelfRemove ? null : (
        <IconStopPropagation className="select-icon-stiopropagation">
          <Tooltip
            id={"selectItemIcon" + itemId}
            tooltipTextKey="select-file-icon-tooltip-text"
            start
            small
            className="select-icon-tooltip"
          >
            <div
              className="select-icon"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectItem(itemId)}
            >
              <Icon name="check" white />
            </div>
          </Tooltip>
          <TranscodingElement fileData={getFilesData()[itemId]} />
        </IconStopPropagation>
      )}
    </div>
  );
};

const GridCardFooter = ({ itemId, data, hover, security = {} }) => {
  const [showActions, setShowActions] = useState(false);

  const actionList = useActionList({
    defaultActionList: ["Comment", "Share", "RemoveFile"],
    security,
  });

  const { mobileCondition } = useMediaQueries();

  const isSelfRemove = useSelfRemovalLink();

  return !mobileCondition ? (
    <AnimationContainer
      animationOn={hover}
      animationClassName="hover-item-fade"
    >
      <div className={`bottom-section ${data[itemId].Type}`}>
        <div className="left-section">
          {data[itemId].contributorName ? (
            <Tooltip tooltipText={data[itemId].contributorName} top left>
              <p>
                &#x200E;
                {data[itemId].contributorName}
              </p>
            </Tooltip>
          ) : null}
          <Tooltip tooltipText={data[itemId].Name} top left>
            &#x200E;{data[itemId].Name}
          </Tooltip>
        </div>
        {isSelfRemove ? null : (
          <div
            className="right-section"
            onMouseLeave={() => setShowActions(false)}
          >
            {actionList.length < ACTIONS_NUMBER_LIMIT ? (
              <FilesActionGroup
                actionList={[...actionList, "Download"]}
                itemId={itemId}
              />
            ) : (
              <>
                <Icon
                  name="dots-vertical"
                  gray
                  clickable
                  onMouseEnter={() => setShowActions(true)}
                />
                <IconStopPropagation>
                  <DownloadActionComponent itemId={itemId} />
                </IconStopPropagation>
              </>
            )}
            <CustomPopup showState={showActions} topPosition={40}>
              <FilesActionGroup {...{ actionList, itemId }} withTitle />
            </CustomPopup>
          </div>
        )}
      </div>
    </AnimationContainer>
  ) : null;
};

const GridItem = ({ itemId, onClick, inFeed }) => {
  const { getFilesData, getMailMetaData, getMailSecurity } = useMail();
  const { getFilesSelection, toggleFileSelect, getFileSelected } =
    useMailView();
  const { mobileCondition } = useMediaQueries();

  const externalHandleClick = onClick
    ? onClick
    : getFilesSelection().length > 0 && mobileCondition
    ? toggleFileSelect
    : false;

  const [hover, setHover] = useState(false);

  const style = getMailMetaData().isWithRatio
    ? {}
    : mobileCondition || !getFilesData()[itemId]
    ? {}
    : getFilesData()[itemId].Type === "Video" && getFilesData()[itemId].ThumbSrc
    ? {}
    : getFilesData()[itemId].Type !== "Image"
    ? { minWidth: "320px", maxWidth: "320px" }
    : {};

  return useMemo(() => {
    return getFilesData()[itemId] ? (
      <EnterControl {...{ setHover, itemId, externalHandleClick, style }}>
        <AnimationContainer
          animationOn={hover || getFileSelected(itemId)}
          animationClassName="hover-item-fade"
        >
          <div className="transparent-overlay" />
        </AnimationContainer>
        {fileTypeGridElements(getFilesData()[itemId], itemId, inFeed)}
        <GridCardHeader {...{ itemId, hover }} />
        <GridCardFooter
          {...{ itemId, hover }}
          data={getFilesData()}
          security={getMailSecurity()}
        />
        {getFilesData()[itemId].Type !== "Image" && !inFeed ? (
          getFilesData()[itemId].Type === "Video" &&
          getFilesData()[itemId].ThumbSrc ? null : (
            <AnimationContainer
              animationOn={!hover}
              animationClassName="hover-item-fade"
            >
              <div className="grid-card-footer">
                <span>&#x200E;{getFilesData()[itemId].Name}</span>
              </div>
            </AnimationContainer>
          )
        ) : null}
      </EnterControl>
    ) : null;
  }, [hover, getFileSelected(itemId)]);
};

export default GridItem;
